import type { FluxErrorEntityErrorCodes } from '../types.js';
import { AutomationV2ExecutionActionAddCardAssigneesCodes } from './automation-v2-execution-action/add-card-assignees.codes.js';
import { AutomationV2ExecutionActionAddCardCommentCodes } from './automation-v2-execution-action/add-card-comment.codes.js';
import { AutomationV2ExecutionActionAddCardLabelCodes } from './automation-v2-execution-action/add-card-label.codes.js';
import { AutomationV2ExecutionActionAddCustomVariableCodes } from './automation-v2-execution-action/add-custom-variables.codes.js';
import { AutomationV2ExecutionActionCreateCardDeadlineCodes } from './automation-v2-execution-action/create-card-deadline.codes.js';
import { AutomationV2ExecutionActionCreateCardReviewCodes } from './automation-v2-execution-action/create-card-review.codes.js';
import { AutomationV2ExecutionActionCreateCardTaskCodes } from './automation-v2-execution-action/create-card-task.codes.js';
import { AutomationV2ExecutionActionGenerateDocumentCodes } from './automation-v2-execution-action/generate-document.codes.js';
import { AutomationV2ExecutionActionMakeHttpRequestCodes } from './automation-v2-execution-action/make-http-request.codes.js';
import { AutomationV2ExecutionActionMoveCardToStageCodes } from './automation-v2-execution-action/move-card-to-stage.codes.js';
import { AutomationV2ExecutionActionRemoveCardAssigneesCodes } from './automation-v2-execution-action/remove-card-assignees.codes.js';
import { AutomationV2ExecutionActionRemoveCardLabelCodes } from './automation-v2-execution-action/remove-card-label.codes.js';
import { AutomationV2ExecutionActionRequestSolicitationChangesCodes } from './automation-v2-execution-action/request-solicitation-changes.codes.js';
import { AutomationV2ExecutionActionResubmitForApprovalCodes } from './automation-v2-execution-action/resubmit-for-approval.codes.js';
import { AutomationV2ExecutionActionSendEmailCodes } from './automation-v2-execution-action/send-email.codes.js';
import { AutomationV2ExecutionActionToggleCardArchivedCodes } from './automation-v2-execution-action/toggle-card-archived.codes.js';
import { AutomationV2ExecutionActionToggleCardLockedCodes } from './automation-v2-execution-action/toggle-card-locked.codes.js';
import { AutomationV2ExecutionActionUpdateCardFieldValueCodes } from './automation-v2-execution-action/update-card-field-value.codes.js';
import { AutomationV2ExecutionActionUpdateCardNameCodes } from './automation-v2-execution-action/update-card-name.codes.js';
import { AutomationV2ExecutionActionUpdateCardTaskStatusCodes } from './automation-v2-execution-action/update-card-task-status.codes.js';

export const AutomationV2ActionErrorCodes = {
  AutomationV2ActionInvalidProperties: {
    code: 'automation-v2-action/invalid-properties',
    explanation: `As propriedades configuradas para esta ação **não são válidas**.

**Causas comuns:**
- Campos obrigatórios da ação não foram preenchidos
- Tipos de dados incorretos (ex: texto onde se esperava número)
- Referências a IDs que não existem mais

**Como diagnosticar:**
Revise a configuração da ação na interface de automação e certifique-se de que todos os campos obrigatórios estão preenchidos corretamente.`,
    localizations: {},
    message:
      'As propriedades da ação de automação são inválidas. Verifique os dados e tente novamente.',
    supportingDocs: [
      {
        name: 'Configuração de Ações',
        path: '/wiki/docs/automations/criando-sua-automacao',
      },
    ],
  },
  AutomationV2ActionNotFound: {
    code: 'automation-v2-action/not-found',
    explanation: `A ação solicitada não foi encontrada na definição da automação.

**Causas comuns:**
- A ação foi **excluída** recentemente
- Erro de sincronização entre a versão editada e a salva

**Como resolver:**
Recarregue a automação. Se o erro persistir, remova e adicione a ação novamente.`,
    localizations: {},
    message: 'A ação da automação não foi encontrada.',
    supportingDocs: [
      {
        name: 'Ações de Automação',
        path: '/wiki/docs/category/ações',
      },
    ],
  },
  AutomationV2ActionNotFoundInVersion: {
    code: 'automation-v2-action/not-found-in-version',
    explanation: `A ação referenciada não existe na **versão atual** da automação.

**Causas comuns:**
- Tentativa de acessar uma ação de uma versão antiga que foi removida na nova versão
- Inconsistência no versionamento da automação

**Como diagnosticar:**
Verifique o histórico de versões da automação.`,
    localizations: {},
    message: 'A ação especificada não foi encontrada nesta versão da automação.',
  },
  AutomationV2ActionTypeNotFound: {
    code: 'automation-v2-action/type-not-found',
    explanation: `O **tipo de ação** especificado é desconhecido pelo sistema.

**Causas comuns:**
- O tipo de ação foi descontinuado
- Erro na digitação do identificador do tipo de ação (payload manual)
- Feature flag desabilitada para este tipo de ação

**Como resolver:**
Selecione um tipo de ação válido disponível no menu de ações.`,
    localizations: {},
    message:
      'O tipo de ação especificado não foi encontrado ou não está disponível. Verifique se o tipo de ação está correto.',
    supportingDocs: [
      {
        name: 'Ações de Automação',
        path: '/wiki/docs/category/ações',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-action'>;

export const AutomationV2AutocompleteErrorCodes = {
  AutomationV2AutocompleteNotFound: {
    code: 'automation-v2-autocomplete/not-found',
    explanation: `O recurso alvo do autocomplete não retornou resultados.

**Causas comuns:**
- A entidade buscada não existe
- Termo de busca muito específico sem correspondência

**Como resolver:**
Tente termos de busca mais genéricos.`,
    localizations: {},
    message: 'A entidade solicitada para completar não foi encontrada.',
  },
  AutomationV2AutocompletePipeIdRequired: {
    code: 'automation-v2-autocomplete/pipe-id-required',
    explanation: `O autocomplete depende de um contexto de **Pipe (Fluxo)** que não foi fornecido.

**Causas comuns:**
- Chamada de API sem o parâmetro \`pipeId\`
- Uso do autocomplete fora do contexto de um fluxo

**Como resolver:**
Forneça o ID do fluxo onde a automação está sendo executada.`,
    localizations: {},
    message: 'Esse tipo de autocomplete requer um ID de fluxo para funcionar corretamente.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-autocomplete'>;

export const AutomationV2ErrorCodes = {
  AutomationV2NotFound: {
    code: 'automation-v2/not-found',
    explanation: `A automação solicitada não existe.

**Causas comuns:**
- ID de automação incorreto
- Automação excluída
- Falta de permissão de acesso

**Como diagnosticar:**
Verifique se a automação aparece na lista de automações do fluxo.`,
    localizations: {},
    message: 'A automação não foi encontrada.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2'>;

export const AutomationV2ExecutionActionErrorCodes = {
  ...AutomationV2ExecutionActionAddCardAssigneesCodes,
  ...AutomationV2ExecutionActionAddCardCommentCodes,
  ...AutomationV2ExecutionActionAddCardLabelCodes,
  ...AutomationV2ExecutionActionAddCustomVariableCodes,
  ...AutomationV2ExecutionActionCreateCardDeadlineCodes,
  ...AutomationV2ExecutionActionCreateCardReviewCodes,
  ...AutomationV2ExecutionActionCreateCardTaskCodes,
  ...AutomationV2ExecutionActionGenerateDocumentCodes,
  ...AutomationV2ExecutionActionMakeHttpRequestCodes,
  ...AutomationV2ExecutionActionMoveCardToStageCodes,
  ...AutomationV2ExecutionActionRemoveCardAssigneesCodes,
  ...AutomationV2ExecutionActionRemoveCardLabelCodes,
  ...AutomationV2ExecutionActionRequestSolicitationChangesCodes,
  ...AutomationV2ExecutionActionResubmitForApprovalCodes,
  ...AutomationV2ExecutionActionSendEmailCodes,
  ...AutomationV2ExecutionActionToggleCardArchivedCodes,
  ...AutomationV2ExecutionActionToggleCardLockedCodes,
  ...AutomationV2ExecutionActionUpdateCardFieldValueCodes,
  ...AutomationV2ExecutionActionUpdateCardNameCodes,
  ...AutomationV2ExecutionActionUpdateCardTaskStatusCodes,
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution-action'>;

export const AutomationV2ExecutionActionGenericErrorCodes = {
  AutomationV2ExecutionActionGenericAttemptToCastValueFailed: {
    code: 'automation-v2-execution-action-generic/attempt-to-cast-value-failed',
    explanation: `Falha de conversão de tipos(Type Casting).

** Causas comuns:**
    - Tentar usar texto em campos numéricos
- Formato de data inválido

** Como resolver:**
  Garanta que os dados passados para a ação correspondam aos tipos esperados(Número, Texto, Data, Booleano).`,
    localizations: {},
    message:
      'Não foi possível converter um valor da automação para o tipo de dados esperado. Verifique os dados.',
    supportingDocs: [
      {
        name: 'Conversão de Valores',
        path: '/wiki/docs/automations/convercao-de-valores',
      },
      {
        name: 'Conversão de Valores nas Condições',
        path: '/wiki/docs/automations/condicoes#conversão-de-valores',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution-action-generic'>;

export const AutomationV2ExecutionErrorCodes = {
  AutomationV2ExecutionAutomationIdRequired: {
    code: 'automation-v2-execution/automation-id-required',
    explanation: `ID da automação não encontrado no contexto de execução.

** Causas comuns:**
  - Erro interno de contexto
    - Chamada direta de execução sem passar metadados

      ** Como diagnosticar:**
        Isso geralmente é um erro interno ou de integração via API.`,
    localizations: {},
    message: 'O ID da automação é obrigatório para executar esta ação.',
  },
  AutomationV2ExecutionInvalidOperation: {
    code: 'automation-v2-execution/invalid-operation',
    explanation: `A operação tentada não é permitida no estado atual.

** Causas comuns:**
  - Ação incompatível com o tipo de gatilho
    - Tentativa de modificar dados somente leitura

      ** Como resolver:**
        Revise a lógica da automação.`,
    localizations: {},
    message: 'Operação inválida durante a execução da automação.',
  },
  AutomationV2ExecutionNotFound: {
    code: 'automation-v2-execution/not-found',
    explanation: `O registro da execução não foi encontrado.

** Causas comuns:**
  - ID de execução inválido
    - Logs de execução expirados

      ** Como diagnosticar:**
        Verifique se o ID da execução está correto.`,
    localizations: {},
    message: 'Execução da automação não encontrada.',
  },
  AutomationV2ExecutionRecursiveLimitReached: {
    code: 'automation-v2-execution/recursive-limit-reached',
    explanation: `A proteção contra loops infinitos foi ativada.

** Causas comuns:**
  - Automação A dispara Automação B que dispara Automação A
    - Trigger "Quando card for movido" movendo o card para a mesma fase

      ** Como resolver:**
        Revise a lógica para evitar ciclos de disparo contínuo.`,
    localizations: {},
    message: 'A automação atingiu o limite de execuções encadeadas (loop infinito detectado).',
    supportingDocs: [
      {
        name: 'Limites das Automações',
        path: '/wiki/docs/automations/limites-das-automacoes',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution'>;

export const AutomationV2RoleErrorCodes = {
  AutomationV2RoleAlreadyExists: {
    code: 'automation-v2-role/already-exists',
    explanation: `Conflito de papéis(Roles).

** Causas comuns:**
  - Tentativa de adicionar um papel duplicado

    ** Como resolver:**
      Use o papel já existente.`,
    localizations: {},
    message: 'Já existe um papel designado para esta automação nesse fluxo.',
  },
  AutomationV2RoleForbiddenInDatabase: {
    code: 'automation-v2-role/forbidden-in-database',
    explanation: `Papel inválido para contexto de Banco de Dados.

** Causas comuns:**
  - Papel exclusivo de pipes usado em banco de dados

    ** Como resolver:**
      Escolha um papel compatível com bancos de dados.`,
    localizations: {},
    message: 'Este cargo de banco de dados não pode ser atribuído a automações.',
    supportingDocs: [
      {
        name: 'Permissões de Automação',
        path: '/wiki/docs/automations/seguranca-e-permissoes',
      },
      {
        name: 'Cargos e Permissões',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  AutomationV2RoleForbiddenInGlobal: {
    code: 'automation-v2-role/forbidden-in-global',
    explanation: `Papel inválido para contexto Global.

** Causas comuns:**
  - Restrição de segurança para automações globais

    ** Como resolver:**
      Use um papel permitido para automações globais.`,
    localizations: {},
    message: 'Este cargo global não pode ser atribuído a automações.',
    supportingDocs: [
      {
        name: 'Permissões de Automação',
        path: '/wiki/docs/automations/seguranca-e-permissoes',
      },
      {
        name: 'Cargos e Permissões',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  AutomationV2RoleForbiddenInPipe: {
    code: 'automation-v2-role/forbidden-in-pipe',
    explanation: `Papel inválido para contexto de Pipe.

** Causas comuns:**
  - Papel exclusivo de banco de dados usado em pipe

    ** Como resolver:**
      Escolha um papel compatível com fluxos(pipes).`,
    localizations: {},
    message: 'Este cargo de fluxo não pode ser atribuído a automações.',
    supportingDocs: [
      {
        name: 'Permissões de Automação',
        path: '/wiki/docs/automations/seguranca-e-permissoes',
      },
      {
        name: 'Cargos e Permissões',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  AutomationV2RoleNotFound: {
    code: 'automation-v2-role/not-found',
    explanation: `Role não existe.

** Causas comuns:**
  - Papel excluído
    - ID incorreto

      ** Como diagnosticar:**
        Verifique a lista de papéis disponíveis.`,
    localizations: {},
    message: 'Papel da automação não encontrado.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-role'>;

export const AutomationV2TriggerConditionErrorCodes = {
  AutomationV2TriggerConditionBothValueAndValues: {
    code: 'automation-v2-trigger-condition/both-value-and-values',
    explanation: `Configuração de condição ambígua.

** Causas comuns:**
  - Definir \`value\` (singular) e \`values\` (plural) simultaneamente no JSON

**Como resolver:**
Use apenas um dos campos, dependendo do operador (ex: "igual" usa value, "está contido em" usa values).`,
    localizations: {},
    message: 'Não é permitido informar "valor" e "valores" ao mesmo tempo na condição do gatilho.',
    supportingDocs: [
      {
        name: 'Criando sua Automação',
        path: '/wiki/docs/automations/criando-sua-automacao',
      },
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionExpectedStringOnOperator: {
    code: 'automation-v2-trigger-condition/expected-string-on-operator',
    explanation: `Tipo de dado incorreto para o operador.

**Causas comuns:**
- Usar número ou booleano em operadores de texto (ex: "começa com")

**Como resolver:**
Forneça um valor de texto (string).`,
    localizations: {},
    message: 'O operador da condição do gatilho espera um valor do tipo texto.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionNoValueExpectedForOperator: {
    code: 'automation-v2-trigger-condition/no-value-expected-for-operator',
    explanation: `Valor fornecido onde não era esperado.

**Causas comuns:**
- Passar valor para operadores unários como "está vazio" ou "é nulo"

**Como resolver:**
Remova o campo de valor da configuração da condição.`,
    localizations: {},
    message: 'O operador da condição do gatilho não espera nenhum valor.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionNoValueOrValuesProvided: {
    code: 'automation-v2-trigger-condition/no-value-or-values-provided',
    explanation: `Valor ausente para a condição.

**Causas comuns:**
- Campos \`value\` ou \`values\` não preenchidos para operadores que exigem comparação (ex: "igual a")

**Como resolver:**
Forneça o valor a ser comparado.`,
    localizations: {},
    message: 'Nenhum valor foi informado para a condição do gatilho.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionNotFound: {
    code: 'automation-v2-trigger-condition/not-found',
    explanation: `Condição inexistente.

**Causas comuns:**
- ID de condição inválido

**Como diagnosticar:**
Verifique a estrutura do gatilho.`,
    localizations: {},
    message: 'Condição do gatilho não encontrada.',
  },
  AutomationV2TriggerConditionNotFoundForTriggerType: {
    code: 'automation-v2-trigger-condition/not-found-for-trigger-type',
    explanation: `Condição incompatível com o Trigger.

**Causas comuns:**
- Tentar usar condição de campo de data em gatilho que não tem contexto de data

**Como resolver:**
Use apenas condições suportadas pelo tipo de gatilho selecionado.`,
    localizations: {},
    message: 'A condição do gatilho não foi encontrada para o tipo de gatilho especificado.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionOperatorNotAllowed: {
    code: 'automation-v2-trigger-condition/operator-not-allowed',
    explanation: `Operador inválido.

**Causas comuns:**
- Usar "maior que" em campos de texto

**Como resolver:**
Selecione um operador compatível com o tipo de dado do campo.`,
    localizations: {},
    message: 'O operador especificado não é permitido para este tipo de condição.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
  AutomationV2TriggerConditionValueNotAllowed: {
    code: 'automation-v2-trigger-condition/value-not-allowed',
    explanation: `Valor proibido.

**Causas comuns:**
- Valores fora do range permitido
- Caracteres inválidos

**Como resolver:**
Verifique as regras de validação da condição.`,
    localizations: {},
    message: 'O valor especificado não é permitido para este tipo de condição.',
    supportingDocs: [
      {
        name: 'Condições',
        path: '/wiki/docs/automations/condicoes',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-trigger-condition'>;

export const AutomationV2TriggerErrorCodes = {
  AutomationV2TriggerInvalidProperties: {
    code: 'automation-v2-trigger/invalid-properties',
    explanation: `Configuração de gatilho inválida.

**Causas comuns:**
- Campos obrigatórios do gatilho faltando
- Configuração de evento inconsistente

**Como diagnosticar:**
Revise a configuração do gatilho.`,
    localizations: {},
    message: 'As propriedades do gatilho da automação são inválidas.',
    supportingDocs: [
      {
        name: 'Gatilhos da Automação',
        path: '/wiki/docs/category/gatilhos',
      },
    ],
  },
  AutomationV2TriggerNotFound: {
    code: 'automation-v2-trigger/not-found',
    explanation: `Gatilho não encontrado.

**Causas comuns:**
- Gatilho removido
- Erro de ID

**Como resolver:**
Recrie o gatilho se necessário.`,
    localizations: {},
    message: 'O gatilho da automação não foi encontrado.',
  },
  AutomationV2TriggerNotFoundInVersion: {
    code: 'automation-v2-trigger/not-found-in-version',
    explanation: `Gatilho não existe nesta versão.

**Causas comuns:**
- Inconsistência de versionamento

**Como diagnosticar:**
Verifique a versão da automação.`,
    localizations: {},
    message: 'O gatilho especificado não foi encontrado nesta versão da automação.',
  },
  AutomationV2TriggerTypeNotFound: {
    code: 'automation-v2-trigger/type-not-found',
    explanation: `Tipo de gatilho desconhecido.

**Causas comuns:**
- Tipo descontinuado
- Feature flag desativada

**Como resolver:**
Escolha um tipo de gatilho disponível.`,
    localizations: {},
    message: 'O tipo de gatilho especificado não foi encontrado ou não está disponível.',
    supportingDocs: [
      {
        name: 'Gatilhos da Automação',
        path: '/wiki/docs/category/gatilhos',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-trigger'>;

export const AutomationV2VersionErrorCodes = {
  AutomationV2VersionMalformed: {
    code: 'automation-v2-version/malformed',
    explanation: `Dados da versão corrompidos.

**Causas comuns:**
- Erro de salvamento
- JSON inválido na estrutura da versão

**Como resolver:**
Tente salvar uma nova versão da automação.`,
    localizations: {},
    message: 'A versão da automação está malformada. Verifique os dados e tente novamente.',
  },
  AutomationV2VersionNotFound: {
    code: 'automation-v2-version/not-found',
    explanation: `Versão não encontrada.

**Causas comuns:**
- ID de versão incorreto
- Versão excluída

**Como diagnosticar:**
Verifique o histórico de versões.`,
    localizations: {},
    message: 'Versão da automação não encontrada.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-version'>;
